import {
  ApiResponse,
  DEFAULT_DIFFICULTY_SETTINGS,
  DifficultySettings
} from '@unity-learning/shared';
import { getDifficultySettings } from '../services/api';

/**
 * B6 — Découvrabilité des paramètres de difficulté.
 * Compteur de réussites consécutives en localStorage (clé par utilisateur, pas de backend) :
 * - incrémenté à chaque soumission réussie (score >= 70), remis à zéro sur échec réel ;
 * - au seuil (3 réussites d'affilée), le CTA « Trop facile ? » est proposé sur la page
 *   d'exercice, SAUF si les réglages serveur diffèrent déjà des valeurs par défaut
 *   (l'étudiant a trouvé la modale tout seul → le CTA n'a plus de raison d'être) ;
 * - CTA cliqué → plus jamais proposé (l'accès est découvert, objectif atteint) ;
 * - CTA fermé (croix) → re-proposé seulement après 9 réussites consécutives (3 × 3).
 */

const STORAGE_PREFIX = 'difficultyCtaState:';
const INITIAL_THRESHOLD = 3;
const DISMISSED_THRESHOLD = 9;

interface DifficultyCtaState {
  streak: number;      // réussites consécutives depuis le dernier échec
  threshold: number;   // seuil courant d'affichage du CTA
  done: boolean;       // cliqué OU réglages déjà personnalisés → ne plus jamais proposer
}

function readState(userId: string): DifficultyCtaState {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + userId);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (
        typeof parsed.streak === 'number' &&
        typeof parsed.threshold === 'number' &&
        typeof parsed.done === 'boolean'
      ) {
        return parsed;
      }
    }
  } catch {
    // Stockage indisponible ou corrompu → état neuf
  }
  return { streak: 0, threshold: INITIAL_THRESHOLD, done: false };
}

function writeState(userId: string, state: DifficultyCtaState): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + userId, JSON.stringify(state));
  } catch {
    // Stockage indisponible → tant pis, le CTA ne doit jamais casser l'UX
  }
}

/** Les réglages serveur diffèrent-ils des valeurs par défaut (tout au minimum) ? */
async function hasCustomDifficultySettings(userId: string): Promise<boolean> {
  try {
    const s = await getDifficultySettings(userId);
    if (!s) return true; // prudence : dans le doute, ne pas afficher le CTA
    return (
      s.algorithmicComplexity !== DEFAULT_DIFFICULTY_SETTINGS.algorithmicComplexity ||
      (s.unityApiKnowledge ?? DEFAULT_DIFFICULTY_SETTINGS.unityApiKnowledge) !== DEFAULT_DIFFICULTY_SETTINGS.unityApiKnowledge ||
      s.codeScope !== DEFAULT_DIFFICULTY_SETTINGS.codeScope ||
      s.cognitiveLoad !== DEFAULT_DIFFICULTY_SETTINGS.cognitiveLoad
    );
  } catch {
    return true;
  }
}

/**
 * Enregistre une réussite et dit s'il faut proposer le CTA.
 * L'appel réseau (vérification des réglages) n'a lieu qu'au seuil atteint.
 */
export async function recordExerciseSuccess(
  userId: string
): Promise<{ propose: boolean; streak: number }> {
  const state = readState(userId);
  state.streak += 1;
  writeState(userId, state);

  if (state.done || state.streak < state.threshold) {
    return { propose: false, streak: state.streak };
  }

  if (await hasCustomDifficultySettings(userId)) {
    state.done = true;
    writeState(userId, state);
    return { propose: false, streak: state.streak };
  }

  return { propose: true, streak: state.streak };
}

/** Échec réel (score < 70) : la série est cassée. */
export function recordExerciseFailure(userId: string): void {
  const state = readState(userId);
  if (state.streak !== 0) {
    state.streak = 0;
    writeState(userId, state);
  }
}

/** CTA cliqué : l'accès est découvert, ne plus jamais proposer. */
export function markDifficultyCtaClicked(userId: string): void {
  const state = readState(userId);
  state.done = true;
  writeState(userId, state);
}

/** CTA fermé à la croix : re-proposer seulement après une longue série (9). */
export function markDifficultyCtaDismissed(userId: string): void {
  const state = readState(userId);
  state.streak = 0;
  state.threshold = DISMISSED_THRESHOLD;
  writeState(userId, state);
}

/** Réglages sauvegardés depuis la modale : le CTA n'a plus de raison d'être. */
export function markDifficultyAdjusted(userId: string): void {
  const state = readState(userId);
  state.done = true;
  writeState(userId, state);
}
